/**
 * El Clapper Arcade inmersivo: pantalla completa real (la ruta vive FUERA del
 * DashboardLayout — sin sidebar, sin topbar, sin recuadros). El botón "Salir"
 * está integrado al HUD del propio juego y llega como `ca:exit` por el bridge.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useArcadeBridge } from './useArcadeBridge';

export function ArcadePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { isLoading, error } = useArcadeBridge(iframeRef);

  // "Salir" del HUD del juego → volver a Learning
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'ca:exit') navigate('/people/learning');
      // Lapicito de un paso dentro del juego → editor con ese paso abierto
      if (e.data?.type === 'ca:edit-step' && e.data.stepId)
        navigate(`/people/learning?tab=editor&step=${encodeURIComponent(e.data.stepId)}`);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {error ? (
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
          <p className="font-mono text-sm text-red-400">
            {t('people_learning.load_error', 'No se pudo cargar el juego')}
          </p>
          <p className="max-w-md text-xs text-neutral-500">{error.message}</p>
          <button
            onClick={() => navigate('/people/learning')}
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 px-4 py-2 text-sm text-neutral-300 hover:border-brand-green hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('people_learning.back', 'Volver a Learning')}
          </button>
        </div>
      ) : isLoading ? (
        // Boot retro a juego con la estética del arcade (nada de recuadros)
        <div className="flex h-full flex-col items-center justify-center gap-5">
          <span className="animate-pulse font-mono text-sm tracking-[0.3em] text-brand-green">
            CARGANDO…
          </span>
          <span className="font-mono text-[10px] tracking-widest text-neutral-600">
            {t('people-learning.arcade.the_clapper_arcade')}
          </span>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src="/clapper-arcade/index.html"
          title={t('people-learning.arcade.the_clapper_arcade_2')}
          className="h-full w-full border-0"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
          allow="autoplay; fullscreen; encrypted-media"
        />
      )}
    </div>
  );
}
